import React, { FC, useState, useRef, useEffect } from 'react'
import CallToAction from '../CallToAction'
import { ICarousel, ISlideState } from './types'
import { useSwipeable } from 'react-swipeable'
import tdcTheme from '@tdcerhverv/mui-theme'

// MUI
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import { grey } from '@mui/material/colors'

// Icons
import Arrow_left from '@tdcerhverv/parrotfish/dist/icons/Icons/Arrow_left.svg'
import Arrow_right from '@tdcerhverv/parrotfish/dist/icons/Icons/Arrow_right.svg'


const Carousel: FC<ICarousel> = ({ children, ...props }) => {
  const { length } = children
  const [windowSize, setWindowSize] = useState<number>(0)
  const [slideElementWidth, setSlideElementWidth] = useState<number>(0)
  const [slideState, setSlideState] = useState<ISlideState>({
    slidePosition: 0,
    activeSlide: 0,
  })

  const ref = useRef<HTMLDivElement>(null)

  const controlPosition = new Map()

  controlPosition.set('LEFT', 'flex-start')
  controlPosition.set('RIGHT', 'flex-end')
  controlPosition.set('CENTER', 'center')
  controlPosition.set('SPACED', 'space-between')

  const { breakpoints } = useTheme()

  const styles = {
    root: {
      overflow: 'hidden',
      position: 'relative',
      padding: '10px',
      margin: '-10px',
      [breakpoints.up('lg')]: {
        '&:before': {
          content: "''",
          position: 'absolute',
          top: 0,
          left: 0,
          width: '336px',
          height: '100%',
          background: `linear-gradient(90deg, #FFFFFF 0%, rgba(255, 255, 255, 0.63) 47.4%, rgba(255, 255, 255, 0) 100%)`,
          zIndex: 50,
          display: props.overflow ? 'block' : 'none',
        },
        '&:after': {
          content: "''",
          position: 'absolute',
          top: 0,
          right: 0,
          width: '336px',
          height: '100%',
          background: `linear-gradient(270deg, #FFFFFF 0%, rgba(255, 255, 255, 0.63) 47.4%, rgba(255, 255, 255, 0) 100%)`,
          zIndex: 50,
          display: props.overflow ? 'block' : 'none',
        },
      },
    },

    container: {
      padding: 0,
    },
    slider: {
      width: `100%`,
      overflow: 'visible',
      [breakpoints.down('lg')]: {
        overflow: 'hidden',
      },
    },
    sliderContainer: {
      display: 'grid',
      gridGap: props.slideGap,
      gridTemplateRows: 'none',
      gridAutoFlow: 'column',
      gridAutoColumns: `calc((100% - (${props.numberOfSlidesInView} - 1) * ${props.slideGap}px) / ${props.numberOfSlidesInView})`,
      margin: 0,
      padding: 0,
      marginBottom: '40px',
      minHeight: '200px',
      transition: `transform 300ms ease`,
      transform: `translateX(-${slideState.slidePosition}px)`,
      [breakpoints.down('lg')]: {
        gridAutoColumns: `calc((100% - (${
          props.numberOfSlidesInView === 1 ? 1 : 2
        } - 1) * ${props.slideGap}px) / ${
          props.numberOfSlidesInView === 1 ? 1 : 2
        })`,
      },
      [breakpoints.down('md')]: {
        gridAutoColumns: `calc((100% - (1 - 1) * ${props.slideGap}px) / 1)`,
      },
    },
    controlsContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      '& > *': {},
      [breakpoints.up('sm')]: {
        '& > *:not(a)': {
          zIndex: 100,
          flex: '1 0 50%',
        },
      },
    },
    bulletContainer: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    sliderBullet: {
      position: 'relative',
      padding: '15px',
      borderRadius: '50px',
      cursor: 'pointer',
      transition: 'all 300ms ease',
      '& span': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%)`,
        display: 'block',
        backgroundColor: grey[500],
        width: '4px',
        height: '4px',
        borderRadius: '50px',
        transition: 'all 300ms ease',
      },

      '&:first-child': {
        marginLeft: 0,
      },
      '&:last-child': {
        marginRight: 0,
      },

      '&:hover': {
        backgroundColor: grey[200],
        '& span': {
          backgroundColor: 'primary.dark',
          width: '8px',
          height: '8px',
        },
      },
    },

    activeBullet: {
      '& span': {
        backgroundColor: 'primary.dark',
        width: '8px',
        height: '8px',
      },
    },
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth)
    }

    // Add event listener
    window.addEventListener('resize', handleResize)
    // Call handler right away so state gets updated with initial window size
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setSlideState({
      slidePosition: 0,
      activeSlide: 0,
    })
    setSlideElementWidth(
      ref.current?.children[0].children[0].children[0].getBoundingClientRect()
        .width ?? 0, // Getting the exact width of the slide element widthin the carousel
    )
  }, [windowSize, props.numberOfSlidesInView,props.overflow])

  const getAllSlideLength = () => {
    let slideLength = props.numberOfSlidesInView ?? 4

    if (
      windowSize < tdcTheme.breakpoints.values.lg &&
      windowSize > tdcTheme.breakpoints.values.md &&
      props.numberOfSlidesInView !== 1
    ) {
      slideLength = 2
    } else if (windowSize < tdcTheme.breakpoints.values.md) {
      slideLength = 1
    }

    return (
      (slideElementWidth + (props?.slideGap ?? 24)) * (length - slideLength) -
      (props.slideGap ?? 24) * 2
    )
  }

  const nextSlide = () => {
    slideState.slidePosition <= getAllSlideLength() &&
      setSlideState({
        slidePosition:
          slideState.slidePosition +
          (slideElementWidth + (props.slideGap ?? 24)),
        activeSlide: slideState.activeSlide + 1,
      })
  }
  const previousSlide = () => {
    slideState.slidePosition > 0 &&
      setSlideState({
        slidePosition:
          slideState.slidePosition -
          (slideElementWidth + (props.slideGap ?? 24)),
        activeSlide: slideState.activeSlide - 1,
      })
  }

  const setSlide = (value: number) => {
    setSlideState({
      slidePosition: (slideElementWidth + (props.slideGap ?? 24)) * value,
      activeSlide: value,
    })
  }
  const swipeHandler = useSwipeable({
    onSwipedLeft: () => nextSlide(),
    onSwipedRight: () => previousSlide(),
    delta: 10,
    //In react-swipeable v-7 this preventDefaultTouchmoveEvent is not present,so adding  preventScrollOnSwipe instead .
    //preventDefaultTouchmoveEvent: true,
    preventScrollOnSwipe: true,
    trackMouse: false,
  })

  const Bullets = (): JSX.Element => {
    return (
      <Box sx={styles.bulletContainer}>
        {Array.from(Array(length)).map((_bullet, i) => {
          return (
            <Box
              key={i}
              id={`carousel-bullet-${i}`}
              role="button"
              aria-label={`Knap til slide ${i + 1}`}
              sx={[
                styles.sliderBullet,
                slideState.activeSlide === i ? styles.activeBullet : {},
              ]}
              onClick={() => setSlide(i)}
            >
              <span></span>
            </Box>
          )
        })}
      </Box>
    )
  }

  return (
    <Box
      aria-roledescription="carousel"
      aria-label={props.title}
      sx={styles.root}
      {...swipeHandler}
    >
      <Container
        disableGutters
        maxWidth={props.overflow ? 'sm' : false}
        ref={ref}
        sx={styles.container}
      >
        <Box sx={styles.slider}>
          <Box component="ul" sx={styles.sliderContainer}>
            {children}
          </Box>
        </Box>
        <Box sx={styles.controlsContainer}>
          {props.readmore && (props.numberOfSlidesInView ?? 4) > 1 && (
            <CallToAction size="small" {...props.readmore} />
          )}
          {length > (props.numberOfSlidesInView ?? 4) && (
            <Box
              display="flex"
              justifyContent={
                props.overflow
                  ? 'center'
                  : controlPosition.get(props.controlPosition)
              }
              flex="1"
            >
              <IconButton
                id="carousel-previous-slide"
                onClick={previousSlide}
                disabled={slideState.slidePosition <= 0}
              >
                <SvgIcon
                  component={Arrow_left}
                  viewBox="0 0 50 50"
                />
              </IconButton>
              {!props.disableBullets && props.numberOfSlidesInView === 1 && (
                <Bullets />
              )}
              <IconButton
                id="carousel-next-slide"
                onClick={nextSlide}
                disabled={slideState.slidePosition >= getAllSlideLength()}
              >
                <SvgIcon
                  component={Arrow_right}
                  viewBox="0 0 50 50"
                />
              </IconButton>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  )
}

Carousel.defaultProps = {
  controlPosition: 'RIGHT',
  numberOfSlidesInView: 4,
  slideGap: 24,
  overflow: false,
}

export default Carousel
